import { CampoServico, camposFiltrados } from '@/features/products/utils'
import { PermissionsType } from '@/types/permissions'
import { Shield, Users, FileText, Download, Package, Edit, Trash2 } from 'lucide-react'

export type PermissionsState = PermissionsType & { [K in CampoServico]: boolean }

export const permissionSectors = [
  {
    id: 'gestao',
    title: 'Gestão',
    description: 'Permissões administrativas e de supervisão',
    icon: Shield,
    color: 'bg-blue-50 border-blue-200',
    permissions: [
      {
        key: 'permission_geral',
        label: 'Permissão Geral',
        description: 'Acesso a todos os clientes, mas não a todos os produtos.'
      },
      {
        key: 'permission_product',
        label: 'Permissão de Produto',
        description: 'Acesso a todos os clientes de um produto específico.'
      }
    ]
  },
  {
    id: 'adicionar',
    title: 'Adicionar',
    description: 'Permissões para criar novos registros',
    icon: Users,
    color: 'bg-green-50 border-green-200',
    permissions: [
      { key: 'add_user', label: 'Adicionar Usuário', description: 'Criar novos usuários no sistema' },
      { key: 'add_client', label: 'Adicionar Cliente', description: 'Cadastrar novos clientes' },
      { key: 'add_contract', label: 'Adicionar Contrato', description: 'Cadastrar novos contratos' }
    ]
  },
  {
    id: 'alterar',
    title: 'Alterar',
    description: 'Permissões para editar registros existentes',
    icon: Edit,
    color: 'bg-yellow-50 border-yellow-200',
    permissions: [
      { key: 'update_product', label: 'Alterar Produto', description: 'Editar produtos no sistema' },
      { key: 'update_client', label: 'Alterar Cliente', description: 'Editar clientes' },
      { key: 'update_user', label: 'Alterar Usuário', description: 'Editar usuário' },
      { key: 'update_tenant', label: 'Alterar Empresa', description: 'Editar informações da empresa' },
      { key: 'update_analise', label: 'Alterar Análise', description: 'Editar análises' },
      { key: 'update_agreement', label: 'Alterar Minuta', description: 'Editar Minuta' },
      { key: 'update_permissions', label: 'Alterar permissões', description: 'Editar permissões' },
      {
        key: 'update_user_product',
        label: 'Alterar Usuário ao Produto',
        description: 'Editar qual analista fica responsável por qual produto do cliente'
      },
      {
        key: 'update_user_client',
        label: 'Alterar Usuário ao Cliente',
        description: 'Editar qual Parceiro ou Gerente fica responsável pelo cliente'
      }
    ]
  },
  {
    id: 'excluir',
    title: 'Excluir',
    description: 'Permissões para excluir registros existentes',
    icon: Trash2,
    color: 'bg-red-50 border-red-200',
    permissions: [
      { key: 'delet_product', label: 'Excluir Produto', description: 'Remover produtos do sistema' },
      { key: 'delet_client', label: 'Excluir Cliente', description: 'Remover clientes do sistema' },
      { key: 'delet_user', label: 'Excluir Usuário', description: 'Remover usuários do sistema' },
      { key: 'delet_tenant', label: 'Excluir Empresa', description: 'Remover empresas' },
      { key: 'delet_agreement', label: 'Excluir Minuta', description: 'Remover minutas' },
      { key: 'delet_contract', label: 'Excluir Contrato', description: 'Remover contratos' }
    ]
  },
  {
    id: 'gerar',
    title: 'Gerar',
    description: 'Permissões para geração de documentos',
    icon: FileText,
    color: 'bg-purple-50 border-purple-200',
    permissions: [
      { key: 'gerar_boleto', label: 'Gerar Boleto', description: 'Emitir boletos bancários' },
      { key: 'gerar_analise', label: 'Gerar Análise', description: 'Criar relatórios de análise' },
      { key: 'gerar_apuracao', label: 'Gerar Apuração', description: 'Processar apurações fiscais' },
      { key: 'gerar_nota_fiscal', label: 'Gerar Nota Fiscal', description: 'Emitir notas fiscais' }
    ]
  },
  {
    id: 'exportar',
    title: 'Exportar',
    description: 'Permissões para exportação de dados',
    icon: Download,
    color: 'bg-orange-50 border-orange-200',
    permissions: [
      { key: 'export_analise', label: 'Exportar Análise', description: 'Baixar relatórios de análise' },
      { key: 'export_products', label: 'Exportar Produtos', description: 'Exportar dados de produtos' },
      { key: 'export_nota_fiscal', label: 'Exportar Nota Fiscal', description: 'Baixar notas fiscais' },
      { key: 'export_boleto', label: 'Exportar Boleto', description: 'Exportar boletos' },
      {
        key: 'export_planilha_clients',
        label: 'Exportar Planilha de Clientes',
        description: 'Exportar a planilha XLSX de clientes'
      }
    ]
  },
  {
    id: 'produtos',
    title: 'Produtos',
    description: 'Permissões específicas de produtos fiscais',
    icon: Package,
    color: 'bg-indigo-50 border-indigo-200',
    permissions: [
      {
        key: 'pis_cofins_monofasico_para_sn',
        label: 'Simples Nacional',
        description: 'Monofásico para Simples Nacional'
      },
      { key: 'previdenciario', label: 'Previdenciário', description: 'Gestão previdenciária' },
      { key: 'exclusao_icms', label: 'Exclusão ICMS', description: 'Processos de exclusão de ICMS' },
      { key: 'subvencao', label: 'Subvenção', description: 'Gerenciar subvenções' },
      { key: 'apuracao_st', label: 'Apuração ST', description: 'Apuração de Substituição Tributária' },
      { key: 'equiparacao_hospitalar', label: 'Equiparação Hospitalar', description: 'Benefícios hospitalares' },
      { key: 'negociacao_de_passivos', label: 'Negociação de Passivos', description: 'Negociação de Passivos' },
      { key: 'despesas_operacionais', label: 'Despesas Operacionais', description: 'Despesas Operacionais' },
      { key: 'fcvs', label: 'FCVS', description: 'FCVS' }
    ]
  }
]
export const makeEmpty = (id_user: string): PermissionsState => {
  // 1) base (id, gestor_product, etc)
  const base: PermissionsType = {
    id: '',
    id_user,

    permission_product: false,
    permission_geral: false,
    add_user_product: false,
    add_user_client: false,

    add_client: false,
    add_user: false,
    add_contract: false,

    update_product: false,
    update_user_product: false,
    update_user_client: false,
    update_user: false,
    update_client: false,
    update_tenant: false,
    update_agreement: false,
    update_analise: false,
    update_permissions: false,

    delet_product: false,
    delet_user: false,
    delet_client: false,
    delet_tenant: false,
    delet_agreement: false,
    delet_contract: false,

    gerar_boleto: false,
    gerar_analise: false,
    gerar_apuracao: false,
    gerar_nota_fiscal: false,

    export_analise: false,
    export_products: false,
    export_boleto: false,
    export_nota_fiscal: false,
    export_planilha_clients: false,

    pis_cofins_monofasico_para_sn: false,
    previdenciario: false,
    exclusao_icms: false,
    subvencao: false,
    apuracao_st: false,
    equiparacao_hospitalar: false,
    negociacao_de_passivos: false,
    despesas_operacionais: false,
    fcvs: false
  }

  // 2) dinâmico (todos os campos de serviço)
  const dynamic = Object.fromEntries(camposFiltrados.map((c: CampoServico) => [c, false])) as Record<
    CampoServico,
    boolean
  >

  return {
    ...base,
    ...dynamic
  }
}
